import React, { useEffect, useRef, useState } from "react";
import styled, { css } from "styled-components";
import { motion, useReducedMotion } from "framer-motion";
import { color, ease, font, layout, type } from "./tokens";

/* ---------------------------------------------------------------- Reveal */

/**
 * `?static=1` renders the page at final state with no JS-driven motion —
 * for screenshots, e2e runs and print. Real visitors never hit this.
 */
export const STATIC_MODE =
  typeof window !== "undefined" &&
  new URLSearchParams(window.location.search).has("static");

/**
 * Reversible "is this in the viewport" flag driven by scroll position
 * (not IntersectionObserver — IO callbacks stall in hidden/headless tabs,
 * which would leave sections invisible). Entrances play on the way down
 * and play back out/in again when scrolling up — same bidirectional
 * feel as the scroll-linked showpieces.
 */
export const useReveal = <T extends Element = HTMLDivElement>(
  threshold = 0.88,
) => {
  const ref = useRef<T>(null);
  const [shown, setShown] = useState(STATIC_MODE);

  useEffect(() => {
    if (STATIC_MODE) return;
    const el = ref.current;
    if (!el) return;
    const check = () => {
      const r = el.getBoundingClientRect();
      setShown(r.top < window.innerHeight * threshold && r.bottom > 24);
    };
    check();
    window.addEventListener("scroll", check, { passive: true });
    window.addEventListener("resize", check);
    return () => {
      window.removeEventListener("scroll", check);
      window.removeEventListener("resize", check);
    };
  }, [threshold]);

  return { ref, shown };
};

type RevealProps = {
  children: React.ReactNode;
  delay?: number;
  y?: number;
  className?: string;
  style?: React.CSSProperties;
};

/**
 * One-shot entrance: fade + rise + settle, fired when the element enters
 * the viewport. Opacity-only under prefers-reduced-motion.
 */
export const Reveal: React.FC<RevealProps> = ({
  children,
  delay = 0,
  y = 24,
  className,
  style,
}) => {
  const reduced = useReducedMotion();
  const { ref, shown } = useReveal();
  if (STATIC_MODE) {
    return (
      <div className={className} style={style}>
        {children}
      </div>
    );
  }
  return (
    <motion.div
      ref={ref}
      className={className}
      style={style}
      initial={false}
      animate={
        shown ? { opacity: 1, y: 0 } : { opacity: 0, y: reduced ? 0 : y }
      }
      transition={{
        duration: shown ? 0.85 : 0.45,
        delay: shown ? delay : 0,
        ease: ease.out,
      }}
    >
      {children}
    </motion.div>
  );
};

/* ------------------------------------------------------------ Typography */

export const MonoLabel = styled.span<{ $onInk?: boolean; $accent?: boolean }>`
  font-family: ${font.mono};
  font-size: ${type.mono};
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.09em;
  color: ${(p) =>
    p.$accent ? color.accent : p.$onInk ? color.mutedOnInk : color.muted};
`;

export const DisplayTitle = styled.h2<{ $size?: "display" | "title" }>`
  margin: 0;
  font-family: ${font.display};
  font-size: ${(p) => (p.$size === "display" ? type.display : type.title)};
  font-weight: 400;
  line-height: 0.95;
  letter-spacing: 0.005em;
  text-transform: uppercase;
  text-wrap: balance;
  color: inherit;
`;

const TwoToneWrap = styled.p`
  margin: 0;
  font-family: ${font.body};
  font-size: ${type.statement};
  font-weight: 500;
  line-height: 1.4;
  letter-spacing: -0.01em;
  max-width: 26em;
  color: ${color.ink};
  text-wrap: pretty;
`;

const TwoToneMuted = styled.span`
  color: ${color.muted};
`;

/** Two-tone sentence: first clause ink, continuation muted. */
export const TwoTone: React.FC<{
  ink: React.ReactNode;
  muted?: React.ReactNode;
  className?: string;
}> = ({ ink, muted, className }) => (
  <TwoToneWrap className={className}>
    {ink}
    {muted ? <TwoToneMuted> {muted}</TwoToneMuted> : null}
  </TwoToneWrap>
);

/* ------------------------------------------------------------------ Pill */

export const pillStyles = css<{ $solid?: boolean; $onInk?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 9px 18px;
  border-radius: 999px;
  font-family: ${font.mono};
  font-size: ${type.mono};
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  text-decoration: none;
  cursor: pointer;
  border: 1px solid
    ${(p) => (p.$onInk ? color.lineOnInk : "rgba(14,14,12,0.28)")};
  color: ${(p) => (p.$onInk ? color.paper : color.ink)};
  background: ${(p) => (p.$solid ? color.accent : "transparent")};
  ${(p) =>
    p.$solid &&
    css`
      border-color: ${color.accent};
      color: ${color.ink};
    `}
  transition:
    background-color 0.25s cubic-bezier(0.16, 1, 0.3, 1),
    color 0.25s cubic-bezier(0.16, 1, 0.3, 1),
    border-color 0.25s cubic-bezier(0.16, 1, 0.3, 1);

  &:hover,
  &:focus-visible {
    background: ${color.accent};
    border-color: ${color.accent};
    color: ${color.ink};
  }

  &:focus-visible {
    outline: 2px solid ${color.ink};
    outline-offset: 2px;
  }
`;

export const PillLink = styled.a<{ $solid?: boolean; $onInk?: boolean }>`
  ${pillStyles}
`;

export const PillButton = styled.button<{ $solid?: boolean; $onInk?: boolean }>`
  ${pillStyles}
`;

/* ------------------------------------------------------------ SectionRow */

/**
 * Bodak's three-part row: mono label left, statement centre, meta right.
 * Collapses to a single column on small screens.
 */
export const SectionRow = styled.div`
  display: grid;
  grid-template-columns: minmax(120px, 1fr) minmax(0, 2.4fr) minmax(80px, 0.8fr);
  gap: clamp(20px, 3vw, 48px);
  align-items: start;

  @media (max-width: 860px) {
    grid-template-columns: 1fr;
    gap: 16px;
  }
`;

export const RowMeta = styled.div`
  justify-self: end;
  text-align: right;
  @media (max-width: 860px) {
    justify-self: start;
    text-align: left;
  }
`;

/* --------------------------------------------------------------- Section */

export const Section = styled.section<{ $ink?: boolean }>`
  position: relative;
  background: ${(p) => (p.$ink ? color.ink : color.paper)};
  color: ${(p) => (p.$ink ? color.paper : color.ink)};
`;

export const Inner = styled.div`
  max-width: ${layout.maxWidth};
  margin: 0 auto;
  padding-left: ${layout.gutter};
  padding-right: ${layout.gutter};
`;
